import { isLocalStorageAvailable } from './local-storage-availability'
import { reportError } from '@/services/error-reporting'

const DEFAULT_APP_ENV = 'development'
const environment = import.meta.env.VITE_APP_ENV || DEFAULT_APP_ENV
const VERSION_KEY = `assets-version-${environment}`
const VERSION_LOCK_KEY = `assets-version-lock-${environment}`
const MAX_CHECK_DELAY_MS = 30 * 60 * 1000
const SUSTAINED_FAILURE_THRESHOLD = 5
const TRANSPORT_ERROR_MESSAGES = ['Failed to fetch', 'Load failed']

let baseInterval = 0
let currentDelay = 0
let consecutiveFailures = 0
let sustainedFailureReported = false
let timer: ReturnType<typeof setTimeout> | undefined

export const startVersionChecker = async (): Promise<void> => {
  if (!isLocalStorageAvailable()) {
    return
  }

  const checkInterval = import.meta.env.VITE_ASSETS_CHECK_SECONDS || '300'
  baseInterval = 1000 * parseInt(checkInterval)
  currentDelay = baseInterval
  consecutiveFailures = 0
  sustainedFailureReported = false
  localStorage.removeItem(VERSION_LOCK_KEY)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkAssetsVersion()
    }
  })

  await checkAssetsVersion()
  scheduleNextCheck()
}

function scheduleNextCheck() {
  if (timer) clearTimeout(timer)

  timer = setTimeout(async () => {
    await checkAssetsVersion()
    scheduleNextCheck()
  }, currentDelay)
}

async function checkAssetsVersion() {
  if (localStorage.getItem(VERSION_LOCK_KEY)) return

  localStorage.setItem(VERSION_LOCK_KEY, 'true')

  if (environment && environment !== DEFAULT_APP_ENV) {
    try {
      const localVersion = localStorage.getItem(VERSION_KEY)

      if (localVersion) {
        const response = await fetch(`/version.txt?t=${Date.now()}`)
        const remoteVersion = await response.text()

        if (response.ok) {
          localStorage.removeItem(VERSION_LOCK_KEY)
          recordSuccess()

          if (remoteVersion.length <= 20 && localVersion !== remoteVersion) {
            localStorage.setItem(VERSION_KEY, remoteVersion)
            setTimeout(() => window.location.reload(), 1000)
          }
        } else {
          recordFailure(`Version request failed with status ${response.status}`)
        }
      } else {
        await getAndStoreRemoteVersion()
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      recordFailure(errorMessage)

      if (TRANSPORT_ERROR_MESSAGES.some((message) => errorMessage.includes(message))) return

      reportError(error, {
        name: 'Error checking version',
        context: {
          error,
          environment: environment
        },
        tags: 'version-checker'
      })
    }
  }
}

function recordSuccess() {
  consecutiveFailures = 0
  sustainedFailureReported = false
  currentDelay = baseInterval
}

function recordFailure(lastError: string) {
  localStorage.removeItem(VERSION_LOCK_KEY)
  consecutiveFailures += 1
  currentDelay = Math.min(currentDelay * 2, MAX_CHECK_DELAY_MS)

  if (
    !sustainedFailureReported &&
    consecutiveFailures >= SUSTAINED_FAILURE_THRESHOLD &&
    document.visibilityState === 'visible' &&
    navigator.onLine === true
  ) {
    sustainedFailureReported = true
    reportError(new Error(lastError), {
      name: 'VersionCheckerSustainedFailure',
      context: { lastError, consecutiveFailures, environment },
      tags: 'version-checker'
    })
  }
}

async function getAndStoreRemoteVersion(retries: number = 0) {
  try {
    const response = await fetch(`/version.txt?t=${Date.now()}`)
    const remoteVersion = await response.text()

    if (response.ok && remoteVersion.length <= 20) {
      localStorage.setItem(VERSION_KEY, remoteVersion)
      await checkAssetsVersion()
      return
    }
  } catch (error) {
    // Do nothing
  }

  if (retries > 5) {
    localStorage.removeItem(VERSION_LOCK_KEY)
    return
  } else {
    setTimeout(
      async () => {
        await getAndStoreRemoteVersion(retries + 1)
      },
      1000 * (retries + 1)
    )
  }
}

export const getLocalVersion = (): string => {
  try {
    return localStorage.getItem(VERSION_KEY) || 'no-local-version'
  } catch (_error) {
    return 'unknown-version'
  }
}
